import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/rest";
import type { ParticipantCohortKind, Timeline } from "../api/types";
import { flowRefresher, nextPollMs } from "../lib/participants";
import { setTimelineRefresher } from "../store/dispatch";

/**
 * Ход накопления во времени.
 *
 * Опрос REST плюс стук трейдера — та же пара, что у панели накопления, и по тем
 * же доводам. Отличается ровно одним: СКЛЕЙКОЙ стуков.
 */
export interface TimelineFilter {
  tags: readonly string[];
  notags: readonly string[];
  groups: readonly ParticipantCohortKind[];
  notgroups: readonly ParticipantCohortKind[];
  /** Отмеченные строки таблицы. Сильнее фильтров, как и в строке «Итого». */
  wallets: readonly string[];
}

/**
 * Ключ запроса.
 *
 * Фильтр входит в КЛЮЧ целиком, а не только в запрос: ряды разных составов —
 * разные данные, и общий ключ показал бы линию прежнего отбора под новой
 * подписью до прихода ответа. Списки сортируются: «кит,картель» и
 * «картель,кит» — один и тот же вопрос, и два ключа под него означали бы два
 * запроса и два кэша одного ответа. Тот же довод, что у ключа панели.
 */
export function timelineKey(
  conditionID: string | undefined,
  filter: TimelineFilter,
  step: number | undefined,
) {
  return [
    "timeline",
    conditionID ?? "",
    [...filter.tags].sort().join(","),
    [...filter.notags].sort().join(","),
    [...filter.groups].sort().join(","),
    [...filter.notgroups].sort().join(","),
    [...filter.wallets].sort().join(","),
    step ?? 0,
  ] as const;
}

/**
 * Склейка стуков «окно изменилось» ДЛЯ РЯДА, а не для панели.
 *
 * Секунда, а не сто миллисекунд, как у накопления, и это не осторожность.
 * Ответ ряда весит на два порядка больше свода — триста точек накопления плюс
 * триста образцов котировки, — а в CHANGELOG уже записана цена стука панели:
 * до десяти запросов по 26 КБ/с на вкладку в горячую минуту окна. С той же
 * склейкой ряд стоил бы вдесятеро больше самой панели.
 *
 * Потерять при этом нечего: график читают глазом, и точка, приехавшая на
 * полсекунды позже, на нём неотличима.
 */
export const TIMELINE_REFRESH_MS = 1000;

export function useTimeline(
  conditionID: string | undefined,
  filter: TimelineFilter,
  step?: number,
) {
  return useQuery<Timeline>({
    queryKey: timelineKey(conditionID, filter, step),
    queryFn: () =>
      api
        .timeline(
          conditionID ?? "",
          {
            tags: filter.tags,
            notags: filter.notags,
            groups: filter.groups,
            notgroups: filter.notgroups,
            wallets: filter.wallets,
          },
          step,
        )
        .then((r) => r.timeline),
    enabled: Boolean(conditionID),
    // Прошлый ряд НЕ держится через смену рынка и через смену фильтра: обе
    // подписи под графиком приезжают из ответа, и старая линия под новой
    // подписью — ровно та ошибка прочтения, ради которой фильтр и заведён. В
    // отличие от панели, где placeholderData держит свод между окнами
    // статистики, здесь мигание дешевле неправды: график читают целиком.
    //
    // Такт задаёт СЕРВЕР полем next_in_ms — по тому же доводу, что у панели:
    // срок годности снимка плавает по цене обхода, и константы на этой стороне
    // не существует.
    refetchInterval: (q) => nextPollMs(q.state.data?.next_in_ms),
    // Опрашиваем и в НЕВИДИМОЙ вкладке — по тому же доводу, что панель
    // накопления: встроенный предпросмотр IDE держит документ всегда `hidden`,
    // и без флага график там замирал бы, обновляясь лишь при переключении
    // вкладки. Ряд считается из снимка, который панель и так держит, так что
    // лишний опрос стоит одного прохода по строкам, а не обхода источника.
    refetchIntervalInBackground: true,
  });
}

/**
 * Подписывает перечитывание ряда на стук трейдера «окно изменилось».
 *
 * Ставится один раз на приложение, как useParticipantsRefreshOnFlow, и по тому
 * же доводу: инвалидация по чужому ключу дешёвая — неактивный запрос TanStack
 * Query не перезапрашивает, — а подписка, живущая вместе с панелью, теряла бы
 * стуки на перемонтировании. Стук, не новее уже полученного ряда (epoch/seq в
 * теле), не перечитывает — тот же отсев, что у панели.
 */
export function useTimelineRefreshOnFlow(): void {
  const client = useQueryClient();
  useEffect(() => {
    const refresh = flowRefresher(
      (conditionID) =>
        client
          .getQueriesData<Timeline>({ queryKey: ["timeline", conditionID] })
          .map(([, data]) => data),
      (conditionID) => {
        void client.invalidateQueries({ queryKey: ["timeline", conditionID] });
      },
      TIMELINE_REFRESH_MS,
    );
    setTimelineRefresher(refresh.fire);
    return () => {
      setTimelineRefresher(null);
      refresh.dispose();
    };
  }, [client]);
}
